<template>
  <div class="life-page">
    <section class="life-panel">
      <div class="life-panel__head">
        <h2 class="life-panel__title">码牌查询</h2>
      </div>
      <div class="life-filter">
        <el-input v-model="filters.plateNo" placeholder="码牌编号" clearable style="width: 180px" @keyup.enter.native="search" />
        <el-select v-model="filters.status" placeholder="状态" clearable style="width: 130px">
          <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
        <el-select v-model="filters.mediaType" placeholder="介质" clearable style="width: 110px">
          <el-option label="QR" value="QR" />
          <el-option label="NFC" value="NFC" />
          <el-option label="QR+NFC" value="QR+NFC" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        <el-button icon="el-icon-refresh" @click="reset">重置</el-button>
      </div>
    </section>

    <section class="life-panel">
      <div class="life-panel__head">
        <h2 class="life-panel__title">码牌列表</h2>
      </div>
      <el-table v-loading="loading" :data="rows" border>
        <el-table-column prop="plateNo" label="码牌编号" min-width="140" />
        <el-table-column label="介质" width="90" align="center">
          <template slot-scope="scope">
            <el-tag size="small" type="info">{{ scope.row.mediaType || 'QR' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="batchNo" label="批次号" width="130" />
        <el-table-column prop="plateType" label="类型" width="100" />
        <el-table-column prop="remark" label="备注" width="120" />
        <el-table-column prop="communityName" label="绑定小区" width="140" />
        <el-table-column label="场景" width="150" align="center">
          <template slot-scope="scope">{{ sceneName(scope.row.sceneCode) }}</template>
        </el-table-column>
        <el-table-column prop="displayName" label="展示名称" width="140" />
        <el-table-column prop="installLocation" label="安装位置" width="140" />
        <el-table-column prop="entryUrl" label="入口链接" min-width="200" />
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusTag(scope.row.status)" size="small">
              {{ scope.row.statusName }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="activatedAt" label="激活时间" width="170" />
        <el-table-column label="操作" width="140" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.status === 'ASSIGNED'" type="text" size="small" @click="openEditor(scope.row)">绑定</el-button>
            <el-button v-if="scope.row.status === 'BOUND'" type="text" size="small" @click="openEditor(scope.row)">编辑</el-button>
            <el-button v-if="scope.row.status === 'BOUND'" type="text" size="small" @click="handleUnbind(scope.row)">解绑</el-button>
            <el-button v-if="scope.row.status === 'BOUND'" type="text" size="small" @click="handleDisable(scope.row)">停用</el-button>
            <el-button v-if="scope.row.status === 'DISABLED'" type="text" size="small" @click="handleReassign(scope.row)">恢复</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination class="life-pagination" :current-page.sync="page.current" :page-size="page.size"
        :page-sizes="[10, 20, 50]" :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="changeSize" @current-change="load" />
    </section>

    <el-dialog :title="form.plateId ? '编辑绑定' : '绑定码牌'" :visible.sync="editorVisible" width="520px" @closed="onDialogClosed">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="码牌编号">
          <el-input :value="currentPlateNo" disabled />
        </el-form-item>
        <el-form-item label="绑定小区" prop="communityId">
          <el-select v-model="form.communityId" placeholder="请选择小区" filterable style="width: 100%">
            <el-option v-for="c in communityOptions" :key="c.communityId" :label="c.name" :value="c.communityId" />
          </el-select>
        </el-form-item>
        <el-form-item label="场景" prop="sceneCode">
          <el-select v-model="form.sceneCode" style="width: 100%" @change="onSceneChange">
            <el-option v-for="s in sceneOptions" :key="s.name" :label="s.description || s.name" :value="s.name" />
          </el-select>
        </el-form-item>
        <el-form-item label="展示名称">
          <el-input v-model="form.displayName" maxlength="128" placeholder="如：1号门岗服务码" />
        </el-form-item>
        <el-form-item label="安装位置">
          <el-input v-model="form.installLocation" maxlength="256" placeholder="如：1号门岗" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editorVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { listPlates, updatePlate, bindPlate } from '@/api/aggregateCode/aggregateCodeApi'
import { getMyEnteredCommunitys, getDict } from '@/api/community/communityApi'

function emptyForm() {
  return { plateId: '', communityId: '', sceneCode: 'COMMUNITY_HOME', scenePath: '', displayName: '', installLocation: '' }
}

export default {
  name: 'AggregateCodePlateList',
  data() {
    return {
      loading: false, saving: false, editorVisible: false,
      currentPlateNo: '',
      rows: [], communityOptions: [], sceneOptions: [],
      statusOptions: [
        { label: '已分配', value: 'ASSIGNED' },
        { label: '已绑定', value: 'BOUND' },
        { label: '已停用', value: 'DISABLED' }
      ],
      filters: { plateNo: '', status: '', mediaType: '' },
      page: { current: 1, size: 10, total: 0 },
      form: emptyForm(),
      rules: {
        communityId: [{ required: true, message: '请选择小区', trigger: 'change' }],
        sceneCode: [{ required: true, message: '请选择场景', trigger: 'change' }]
      }
    }
  },
  created() { this.load(); this.loadDicts() },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await listPlates({ ...this.filters, page: this.page.current, row: this.page.size })
        this.rows = res.data || []
        this.page.total = Number(res.total || 0)
      } catch (error) {
        this.$message.error(error.message || '加载码牌列表失败')
      } finally { this.loading = false }
    },
    async loadDicts() {
      try {
        const [commRes, sceneRes] = await Promise.all([
          getMyEnteredCommunitys({ page: 1, row: 200 }),
          getDict('aggregate_code_binding', 'scene_code')
        ])
        this.communityOptions = commRes.communitys || commRes.data || []
        this.sceneOptions = sceneRes || []
      } catch (_) { /* ignore */ }
    },
    search() { this.page.current = 1; this.load() },
    reset() { this.filters = { plateNo: '', status: '', mediaType: '' }; this.search() },
    changeSize(size) { this.page.size = size; this.load() },
    sceneName(code) {
      const s = this.sceneOptions.find(o => o.name === code)
      return s ? (s.description || s.name) : code
    },
    statusTag(s) {
      return { ASSIGNED: 'warning', BOUND: 'success', DISABLED: 'danger' }[s] || 'info'
    },
    async openEditor(row) {
      this.currentPlateNo = row.plateNo
      // populate from existing binding if BOUND
      if (row.status === 'BOUND') {
        this.form = {
          plateId: row.plateId,
          communityId: row.communityId || '',
          sceneCode: row.sceneCode || 'COMMUNITY_HOME',
          scenePath: row.scenePath || '',
          displayName: row.displayName || '',
          installLocation: row.installLocation || ''
        }
      } else {
        this.form = { ...emptyForm(), plateId: row.plateId }
      }
      this.onSceneChange(this.form.sceneCode)
      this.editorVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    onSceneChange(code) {
      const scene = this.sceneOptions.find(s => s.name === code)
      this.form.scenePath = scene ? scene.statusCd : ''
    },
    onDialogClosed() {
      this.$refs.formRef && this.$refs.formRef.resetFields()
    },
    async handleUnbind(row) {
      try {
        await this.$confirm('确认解绑码牌「' + row.plateNo + '」？解绑后入口将失效。', '操作确认', { type: 'warning' })
        await updatePlate({ plateId: row.plateId, status: 'ASSIGNED' })
        this.$message.success('已解绑')
        this.load()
      } catch (error) {
        if (error !== 'cancel') this.$message.error(error.message || '操作失败')
      }
    },
    async handleDisable(row) {
      try {
        await this.$confirm('确认停用码牌「' + row.plateNo + '」？', '操作确认', { type: 'warning' })
        await updatePlate({ plateId: row.plateId, status: 'DISABLED' })
        this.$message.success('已停用')
        this.load()
      } catch (error) {
        if (error !== 'cancel') this.$message.error(error.message || '操作失败')
      }
    },
    async handleReassign(row) {
      try {
        await this.$confirm('确认恢复该码牌？', '操作确认', { type: 'warning' })
        await updatePlate({ plateId: row.plateId, status: 'ASSIGNED' })
        this.$message.success('已恢复')
        this.load()
      } catch (error) {
        if (error !== 'cancel') this.$message.error(error.message || '操作失败')
      }
    },
    submit() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          await bindPlate(this.form)
          this.$message.success('绑定成功')
          this.editorVisible = false
          this.load()
        } catch (error) {
          this.$message.error(error.message || '绑定失败')
        } finally { this.saving = false }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './life.scss';
</style>
